import {get, post, put} from '@stubhub/rest-method';
import {cookies} from '@stubhub/react-cookies';

const EVENT_CACHE_MAX_AGE = process.env.EVENT_CACHE_MAX_AGE || 350000; // 30 minutes
const useMockServer = /^(true|1)$/i.test(process.env.REACT_APP_USE_MOCK_SERVER) &&
  process.env.NODE_ENV !== 'production';
const localMockServer = `http://localhost:${process.env.REACT_APP_MOCK_SERVER_PORT}`;

const PAYMENT_TIMEOUT = 20000;
export function getEvent(eventsId, currentLocale, cg = cookies.get('session_contactGUID')) {
  const ug = cookies.get('session_userGUID');

  return get({
    host: useMockServer ? localMockServer : process.env.REACT_APP_SFR_API_HOST,
    path: process.env.REACT_APP_SFR_API_PATH,
    headers: {
      'Accept-Language': currentLocale
    },
    query: {
      eventIds: eventsId,
      ug,
      cg
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE,
    timeout: 50000
  });
}

export function getContact(ug) {
  return get({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: `/user/customers/v1/${ug}/contactsV2`,
    headers: {
      'Content-Type': 'text/html; charset=utf-8'
    },
    json: true,
    timeout: 50000
  });
}

export function postContact(userGUID, body) {
  return post({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: `/user/customers/v1/${userGUID}/contactsV2`,
    headers: {
      'x-csrf-token': cookies.get('session_CSRFtoken') // Unclear if this is needed
    },
    body: JSON.stringify(body),
    query: {
      userGUID
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE,
    timeout: 10000,
  });
}

export async function getVenueConfiguration(venueConfigId, currentLocale) {
  return await get({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: '/catalog/venues/v3/venueconfigurations',
    headers: {
      'Accept-Language': currentLocale
    },
    query: {
      venueConfigId,
      isSectionZoneRequired: true
      // Shstore: 1
    },
    credentials: 'omit', // Leave cookies out of request. this prevents sending too many headers and receiving error.
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE
  });
}

export async function getVenueConfigMetadata(venueConfigId, currentLocale, {sectionName, rows, ...options}) {
  return await get({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: '/catalog/venues/v3/venueConfigMetadata',
    headers: {
      'Accept-Language': currentLocale
    },
    query: {
      venueConfigId,
      sectionName,
      rows,
      isSectionStemmingRequired: true,
      isSectionNameExactMatch: true,
      // Shstore: 1, // TODO: is this hardcoded? probably needs to be dynamic
      ...options
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE
  });
}

export async function getIntegratedEventInfo(eventId) {
  return await get({
    host: process.env.REACT_APP_API_HOST,
    path: `/partnerintegration/partnerevents/v1/${eventId}`,
    query: {
      eventId
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE
  });
}

export async function validateBarcodes(tickets = []) {
  return await post({
    host: process.env.REACT_APP_API_HOST,
    path: '/inventory/listings/v1/tickets?action=validateBarcodes',
    body: {
      listing: {
        tickets: [...tickets]
      }
    },
    headers: {
      'x-csrf-token': cookies.get('session_CSRFtoken') // This needs to be passed along
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE
  });
}

export async function getPrice({
  eventId,
  amountPerTicket = {
    amount: null,
    currency: null
  },
  section,
  row,
  fulfillmentType
}) {

  return await post({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST, // /bfx-sell/api
    path: '/pricing/aip/v1/price',
    body:  {
      priceRequestList: {
        priceRequest: [
          {
            includePayout: true,
            amountPerTicket: {
              amount: amountPerTicket.amount,
              currency: amountPerTicket.currency
            },
            amountType: 'LISTING_PRICE',
            section,
            row,
            eventId,
            fulfillmentType,
            listingSource: 'INDY',
            requestKey: eventId
          }
        ]
      }
    },
    timeout: 10000,
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE
  });
}



export async function getAutoPricingForEvents(events) {
  return await post({
    host: process.env.REACT_APP_API_HOST,
    path: '/marketplacedynamics/core/v1/inventorypricingconfig/autopricing/',
    body: {
      availabilityRequest: {
        events
      }
    },
    headers: {
      'x-csrf-token': cookies.get('session_CSRFtoken')
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE
  });
}

export async function getAutoPricing(reqData) {
  return getAutoPricingForEvents([reqData]);
}

export async function getMyPaymentInstruments(currency, countryCode) {
  const userGUID = cookies.get('session_userGUID');

  return await get({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: `/user/customers/v1/${userGUID}/paymentInstrumentsV2`,
    query: {
      returnFullContact: 'Y',
      currency,
      countryCode,
      isAllowPayable: 'Y',
      ts: new Date().getTime(),
      shstore: 1
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE,
    timeout: 50000
  });
}

export async function getAvailablePayableInstrumentTypes(eventId) {
  return await get({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: '/payment/payableInstrumentType/v1',
    query: {
      eventId,
      ts: new Date().getTime(),
      shstore: 1
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE,
    timeout: PAYMENT_TIMEOUT
  });
}

export async function createPayPalEmail(currency, emailAddress) {
  const userGUID = cookies.get('session_userGUID');

  return await post({
    host: process.env.REACT_APP_API_HOST,
    path: `/user/customers/v1/${userGUID}/paymentInstrumentsV2`,
    body: {
      paymentInstrumentv2: {
        currency,
        paymentType: 'paypal',
        paypalDetails: {
          emailAddress,
          mode: 'payable'
        }
      }
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE
  });
}

export async function editPayPalEmail(paymentInstrumentId, currency, emailAddress) {
  const userGUID = cookies.get('session_userGUID');

  return await put({
    host: process.env.REACT_APP_API_HOST,
    path: `/user/customers/v1/${userGUID}/paymentInstrumentsV2/${paymentInstrumentId}`,
    body: {
      paymentInstrumentv2: {
        currency,
        paymentType: 'paypal',
        paypalDetails: {
          emailAddress,
          mode: 'payable'
        }
      }
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE
  });
}

export async function createListing({
  eventId,
  section,
  deliveryOption,
  paymentType,
  ticketTraits,
  splitOption,
  splitQuantity,
  pricePerProduct,
  purchasePricePerProduct,
  autoPricingEnabledInd,
  inhandDate,
  faceValue,
  products}) {
  return await post({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: '/inventory/listings/v2',
    headers: {
      'x-csrf-token': cookies.get('session_CSRFtoken')
    },
    body: {
      eventId,
      section,
      deliveryOption,
      status: 'ACTIVE',
      paymentType,
      ticketTraits,
      splitOption,
      splitQuantity,
      pricePerProduct: {...pricePerProduct},
      purchasePricePerProduct,
      autoPricingEnabledInd,
      inhandDate,
      faceValue,
      products
    },
    json: true,
    timeout: Infinity
  });
}

export async function createBulkListing(listings) {
  return await post({
    host: process.env.REACT_APP_SHAPE_API_HOST,
    path: '/infrastructure/batch/v1',
    query: {
      shstore: 1
    },
    headers: {
      'x-csrf-token': cookies.get('session_CSRFtoken')
    },
    body: {
      execMode: 'PARALLEL',
      failMode: 'CONTINUE',
      requests: listings.map((listing, i) => ({
        batchRequestId: i,
        method: 'POST',
        uri: '/inventory/listings/v2',
        body: listing
      }))
    },
    json: true,
    timeout: Infinity
  });
}

export async function getBraintreeToken(currency) {
  return getClientToken(currency);
}

export async function getSellerEligibility(eventId) {
  const userGUID = cookies.get('session_userGUID');

  return await get({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: `/inventory/eligibility/v1/sellereligibility/?eventId=${eventId}&sellerGuid=${userGUID}&listingSource=indy`,
    json: true,
    cache: false
  });
}

/**
 * @param {{
 * companyName:string,
 * name:{firstName:string,lastName:string},
 * address:{line1:string,line2:string,city:string,state:string,country:string,zipOrPostalCode:string},
 * phoneNumber:string,
 * phoneCallingCode:string,
 * email:string
 * }} customerContact
 * @param {string} currency
 * @param {string} braintreeNonce
 * @param {{securityCode:string, cardType:string, expirationDate:string}} cardDetails
 *
 * expirationDate format - MM/YY
 * returns formatted card number string.
 */
export async function createCreditCard(currency, braintreeNonce, cardDetails, customerContact) {
  const userGUID = cookies.get('session_userGUID');

  return await post({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: `/user/customers/v1/${userGUID}/paymentInstrumentsV2`,
    body: {
      paymentInstrumentv2: {
        paymentType: 'creditcard',
        currency,
        customerContact: {
          companyName: customerContact.companyName,
          name: {
            firstName: customerContact.name.firstName,
            lastName: customerContact.name.lastName
          },
          address: {
            line1: customerContact.address.line1,
            line2: customerContact.address.line2,
            city: customerContact.address.city,
            state: customerContact.address.state,
            country: customerContact.address.country,
            zipOrPostalCode: customerContact.address.zipOrPostalCode
          },
          phoneNumber: customerContact.phoneNumber,
          phoneCallingCode: customerContact.phoneCallingCode,
          email: customerContact.email
        },
        cardDetails: {
          securityCode: cardDetails.securityCode,
          cardType: cardDetails.cardType,
          expirationDate: {
            month: cardDetails.expirationDate.split('/')[0],
            year: `20${cardDetails.expirationDate.split('/')[1]}`
          },
          btNonce: braintreeNonce
        },
        appId: 'SFR'
      }
    },
    json: true,
    timeout: PAYMENT_TIMEOUT
  });
}

/**
 * @param ccId
 * @param {{
 * companyName:string,
 * name:{firstName:string,lastName:string},
 * address:{line1:string,line2:string,city:string,state:string,country:string,zipOrPostalCode:string},
 * phoneNumber:string,
 * phoneCallingCode:string,
 * email:string
 * }} customerContact
 * @param {string} currency
 * @param {string} braintreeNonce
 * @param {{securityCode:string, cardType:string, expirationDate:string}} cardDetails
 *
 * expirationDate format - MM/YY
 * returns formatted card number string.
 */
export async function editCreditCard(ccId, currency, cardDetails, customerContact) {
  const userGUID = cookies.get('session_userGUID');

  return await put({
    host: process.env.REACT_APP_API_HOST,
    path: `/user/customers/v1/${userGUID}/paymentInstrumentsV2/${ccId}`,
    body: {
      paymentInstrumentv2: {
        paymentType: 'creditcard',
        currency,
        customerContact: {
          companyName: customerContact.companyName,
          name: {
            firstName: customerContact.name.firstName,
            lastName: customerContact.name.lastName
          },
          address: {
            line1: customerContact.address.line1,
            line2: customerContact.address.line2,
            city: customerContact.address.city,
            state: customerContact.address.state,
            country: customerContact.address.country,
            zipOrPostalCode: customerContact.address.zipOrPostalCode
          },
          phoneNumber: customerContact.phoneNumber,
          phoneCallingCode: customerContact.phoneCallingCode,
          email: customerContact.email
        },
        cardDetails: {
          securityCode: cardDetails.securityCode,
          expirationDate: {
            month: cardDetails.expirationDate.split('/')[0],
            year: `20${cardDetails.expirationDate.split('/')[1]}`
          }
        },
        appId: 'SFR'
      }
    },
    json: true,
    timeout: PAYMENT_TIMEOUT
  });
}


export async function getInstrumentTypesInfo(currency, countryCode) {
  return await get({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: '/payment/receivableInstrumentType/v2',
    query: {
      currency,
      countryCode,
      shstore: 1
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE,
    timeout: PAYMENT_TIMEOUT
  });
}

export async function getPaymentInstrument(paymentInstrumentId, currency) {
  const userGUID = cookies.get('session_userGUID');

  return await get({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: `/user/customers/v1/${userGUID}/paymentInstrumentsV2/${paymentInstrumentId}`,
    query: {
      doccping: true,
      currency,
      shstore: 1
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE,
    timeout: PAYMENT_TIMEOUT
  });
}

/**
 * Either nonce or instrumentGuid is required
 * @param nonce
 * @param instrumentGuid
 * @param currency
 * @param type {string} one of [LISTING_ADD_CARD, LISTING_MODIFY_CARD, LISTING_SELECT_CARD]
 * @returns {Promise<unknown>}
 */
export async function getIs3DSRequired({nonce, instrumentGuid}, {currency, type = 'LISTING_ADD_CARD'}) {
  if (!nonce && !instrumentGuid) {
    throw new Error('nonce or instrumentGuid required');
  }

  return await get({
    host: useMockServer ? localMockServer : location.origin, // Hack to by-pass enabling local auth
    path: '/bfx-payment/api/payments/instruments/receivable/v1/threeDS/isRequired',
    query: {
      eventCurrency: currency,
      amount: 0,
      type
    },
    headers: {
      nonce,
      instrumentGuid
    },
    json: true,
    timeout: 8000
  });
}

export async function getClientToken(currency) {
  return await get({
    host: useMockServer ? localMockServer : location.origin, // Hack to by-pass enabling local auth
    path: '/bfx-payment/api/payments/buyer/gateway/v1/clientToken',
    query: {
      eventCurrency: currency,
      paymentType: 'CreditCard'
    },
    json: true
  });
}

/**
 * @param instrumentGuid
 * @param currency
 * @returns {Promise<{
 *  "nonce": "xxxxx",
 *  "bin": "xxxx",
 *  "regulationEnvironment": "",
 *  "countryOfIssuance": ""
 * }>}
 */
export async function getBraintreeCard(instrumentGuid, currency) {
  return await get({
    host: location.origin, // Hack to by-pass enabling local auth
    path: `/bfx-payment/api/payments/instruments/receivable/v1/${instrumentGuid}/btNonce`,
    query: {
      currencyCode: currency
    },
    json: true,
    timeout: PAYMENT_TIMEOUT
  });
}

export async function update3DSecureForPaymentInstrument(instrumentGuid, nonce, type = 'LISTING_MODIFY_CARD_WEB') {
  return await post({
    host: location.origin, // Hack to by-pass enabling local auth
    path: `/bfx-payment/api/payments/instruments/receivable/v1/${instrumentGuid}/threeDS`,
    body: {
      threeDSNonce: nonce,
      type
    },
    json: true,
    timeout: PAYMENT_TIMEOUT
  });
}

/**
 *
 * @param nonce
 * @returns {
 *  "instrumentGuid": null,
 *  "liabilityShifted": true|false,
 *  "liabilityShiftPossible": true|false,
 *  "enrolled": "Y",
 *  "status": "authenticate_successful",
 *  "isValid": true,
 *  "error": null
 *}
 */
export async function create3DSResult(nonce) {
  return await post({
    host: useMockServer ? localMockServer : location.origin, // Hack to by-pass enabling local auth
    path: '/bfx-payment/api/payments/instruments/receivable/v1/threeDS',
    body: {
      threeDSNonce: nonce,
      type: 'LISTING_ADD_CARD_WEB'
    },
    json: true,
    timeout: PAYMENT_TIMEOUT
  });
}

export async function getAvailableCountriesDetails(countryCode) {
  return await get({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: '/i18n/addresselement/v1/countries',
    query: {
      sortBy: 'countryname',
      venueCountry: countryCode,
      allowSeller: 1
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE
  });
}

export async function getAllStatesByCountryCode(countryCode) {
  return await get({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: `/i18n/addresselement/v1/countries/${countryCode}/states`,
    query: {
      sortBy: 'name'
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE,
    timeout: Infinity
  });
}

export async function salesTaxEligibility(eventId) {
  return await get({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: '/pricing/aip/v1/salestax',
    query: {
      eventId
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE
  });
}

export async function getCatalogEvent(eventId, currentLocale) {
  return await get({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: `/catalog/events/v3/${eventId}`,
    headers: {
      'Accept-Language': currentLocale
    },
    query: {
      mode: 'internal'
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE
  });
}

export async function getPartnerInventory(eventId) {
  return await post({
    host: process.env.REACT_APP_SHAPE_API_HOST,
    path: '/partnerintegration/tickets/v1/inventory',
    headers: {
      'x-csrf-token': cookies.get('session_CSRFtoken') // This needs to be passed along
    },
    body: {
      eventIds:[eventId]
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE
  });
}

export async function getPartnerAccessToken(email, password) {
  return await get({
    host: process.env.REACT_APP_API_HOST,
    path: '/axs/login',
    query: {
      email,
      auth: btoa(`${email}:${password}`)
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE
  });
}


export async function resetPartnerPassword(email) {
  return await get({
    host: process.env.REACT_APP_API_HOST,
    path: '/axs/password/reset',
    query: {
      email
    },
    json: true
  });
}


export async function updateExternalAccount(memberAccessToken, mobileId, externalAccountNumber) {
  return await post({
    host: process.env.REACT_APP_API_HOST,
    path: '/accountmanagement/externalaccount/v1/flashseat',
    headers: {
      'x-csrf-token': cookies.get('session_CSRFtoken') // This needs to be passed along
    },
    body: {
      memberAccessToken,
      mobileId,
      externalAccountNumber
    },
    json: true
  });
}

function privateDueDiligenceType(originCountry, PIProps) {
  const {
    firstName,
    lastName,
    formattedDOB,
    address,
    buildingNum,
    city,
    zipcode,
    state,
    countryCode
  } = PIProps;

  return {
    UsersDueDiligence: [
      {
        countryCode: originCountry,
        level: 'Simple', // This is static
        userDueDiligenceType: 'Individual',
        fields: [
          {
            duediligenceFieldId: 1108,
            value: firstName
          },
          {
            duediligenceFieldId: 1109,
            value: lastName
          },
          {
            duediligenceFieldId: 1110,
            value: formattedDOB
          },
          {
            duediligenceFieldId: 1111,
            value: address
          },
          {
            duediligenceFieldId: 1112,
            value: buildingNum // Optional field
          },
          {
            duediligenceFieldId: 1113,
            value: city
          },
          {
            duediligenceFieldId: 1114,
            value: zipcode
          },
          {
            duediligenceFieldId: 1115,
            value: state
          },
          {
            duediligenceFieldId: 1116,
            value: countryCode
          }
        ]
      }
    ]
  };
}

function businessDueDiligenceType(originCountry, PIProps) {
  const {
    address,
    buildingNum,
    city,
    zipcode,
    state,
    countryCode,
    businessName
  } = PIProps;

  return {
    UsersDueDiligence: [
      {
        countryCode: originCountry,
        level: 'Simple', // This is static
        userDueDiligenceType: 'Corporation',
        fields: [
          {
            duediligenceFieldId: 583,
            value: address
          },
          {
            duediligenceFieldId: 584,
            value: buildingNum // Optional field
          },
          {
            duediligenceFieldId: 585,
            value: city
          },
          {
            duediligenceFieldId: 586,
            value: zipcode
          },
          {
            duediligenceFieldId: 587,
            value: state
          },
          {
            duediligenceFieldId: 588,
            value: countryCode
          },
          {
            duediligenceFieldId: 590,
            value: businessName
          }
        ]
      }
    ]
  };
}

export async function submitPIInfo(originCountry, {sellerType, ...personalInfoData}, userData) {
  const userGUID = cookies.get('session_userGUID');

  const postDataToHW = post.bind(this, {
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: '/customers/duediligence/post',
    headers: {
      'x-csrf-token': cookies.get('session_CSRFtoken') // Unclear if this is needed
    },
    body: {
      UsersDueDiligences: sellerType === 'private' ? privateDueDiligenceType(originCountry, personalInfoData) : businessDueDiligenceType(originCountry, personalInfoData)
    },
    query: {
      userGUID
    },
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE,
    timeout: 10000,
  });
  const {firstName, lastName, address, buildingNum, city, zipcode, state, countryCode} = personalInfoData;
  const {address: {zipOrPostalCode} = {}, phoneCallingCode, phoneNumber, email} = userData;
  const postDataToContacts = postContact.bind(this, userGUID, {
    contactV2: {
      name: {
        firstName,
        lastName,
      },
      address: {
        line1: address,
        line2: buildingNum,
        city,
        state,
        country: countryCode,
        zipOrPostalCode: zipcode,
      },
      phoneCallingCode,
      phoneNumber: phoneNumber.replace(/\D/g, ''),
      email,
    },
  });

  const promisesToCall = [postDataToHW];
  if (!zipOrPostalCode) {
    promisesToCall.push(postDataToContacts); // Just add the contact post if user not already with a primary address
  }

  /*
   * We just care about the status of the HW call, we don't care if the other failed.
   * The user can always go to the contacts section and add the address there
   */
  const [{status, value, reason}] = await Promise.allSettled(promisesToCall.map(fn => fn()));

  if (status === 'rejected') {
   throw new Error(reason);
  } else {
   return value;
  }
}

export function getUserStatuses() {
  const userGUID = cookies.get('session_userGUID');

  return get({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: '/payments/instruments/statuses',
    query: {
      userGUID
    },
    json: true
  });
}

// TODO: delete after BSF / NoCheck solution is replaced with HW
export function getBSFUserStatuses(shstore) {
  const userGUID = cookies.get('session_userGUID');

  return get({
    host: process.env.REACT_APP_SHAPE_API_HOST,
    path: `/user/customers/v1/${userGUID}/statuses`,
    query: {
      shstore
    },
    json: true
  });
}

export const getTaxIdVisibility = (validateNotExist = false) => {
  return get({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: '/accountmanagement/taxid/v1/seller/shouldshow',
    query: {
      validateNotExist
    },
    json: true
  });
};

const taxIdRestOperation = (operation, extraParams = {}) => {
  return operation({
    host: useMockServer ? localMockServer : process.env.REACT_APP_API_HOST,
    path: '/accountmanagement/taxid/v1/seller',
    json: true,
    ...extraParams
  });
};

export const sendTaxId = (taxId) => taxIdRestOperation(post, {body: {taxId}});
export const updateTaxId = (taxId) => taxIdRestOperation(put, {body: {taxId}});

export async function pdfUpload(file, eventId) {
  const url = useMockServer ? localMockServer : `${process.env.REACT_APP_SHAPE_API_HOST}/fulfillment/pdf/v1/files?eventid=${eventId}`;
  const formData = new FormData();
  formData.append('qqfile', file);
  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: {
      'Accept': 'application/json',
      'x-csrf-token': cookies.get('session_CSRFtoken'),
      'x-file-name': file.name,
      'x-requested-with': 'XMLHttpRequest'
    },
    body: formData
  });

  if (response) {
    return response.json();
  }
}
